import React from "react";
import { useState, useMemo, useEffect, useCallback } from "react";
import * as XLSX from 'xlsx';

import "./Grids.css";
import {
  FaSortAmountUpAlt,
  FaSortAmountDown,
  FaSort,
  FaPlusCircle,
  FaFileExcel,
  FaTimesCircle,
  FaCheckCircle,
  FaEnvelope,
  FaPhoneAlt,
  FaRegSadCry,
  FaGhost,
  FaStickyNote,
  FaTrashRestore
} from "react-icons/fa";
import { undeleteClient } from "../../data/clientData";
import { useToast } from "../../contexts/ToastContext";

import { DateFormatter } from "../../data/helper";
import {
  Column,
  Table,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  FilterFn,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import { Tooltip } from "@progress/kendo-react-tooltip";

import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";

import { enquiryGrid, getEnquiryTypes } from "../../data/interfaces";
import { Switch } from "@progress/kendo-react-inputs";
import dayjs from 'dayjs';
import { AccountDetail } from "../../types/accountTable.types";

declare module "@tanstack/table-core" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    numberRange: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

// Simple numberRange filter - not used in this grid but required by the interface
const numberRangeFilter: FilterFn<any> = (row, columnId, value) => {
  return true; // Always return true since we don't use number range filtering in this grid
};

interface AccountsGridProps {
  onData: AccountDetail[];
  showDeleted?: boolean;
  onShowDead: (value: boolean) => void;
  onShowProgressed?: (value: boolean) => void;
  onRefresh?: () => void;
  onAddToggle: () => void;
  onEditToggle: (clientId: number) => void;
  /** False for Accounts.View: no add or restore. */
  canEdit?: boolean;
}

function AccountsGrid(props: AccountsGridProps) {
  const dateFormatter = new DateFormatter();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const { showToast } = useToast();

  console.log('🎨 AccountsGrid RENDER - props.showDeleted:', props.showDeleted, 'data length:', props.onData.length);

  const exportToExcel = () => {
    let date = new Date().toDateString();
    date = dateFormatter.format(date);
    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Convert the data to a worksheet
    const ws = XLSX.utils.json_to_sheet(props.onData);
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "Accounts");
    
    // Write the workbook and trigger a download
    XLSX.writeFile(wb, `accounts_${date}.xlsx`);
  };

  console.log(props.onData);

  // Handle undelete action - defined before columns so it can be used in Actions column
  const handleUndelete = useCallback(async (clientId: number) => {
    try {
      await undeleteClient(clientId);
      showToast('Account restored successfully', 'success');
      // Trigger parent refresh if callback exists
      if (props.onRefresh) {
        await props.onRefresh();
      }
    } catch (error) {
      console.error('Error restoring account:', error);
      showToast('Failed to restore account', 'error');
    }
  }, [props.onRefresh, showToast]);

  const clientColumns = useMemo<ColumnDef<AccountDetail>[]>(
    () => [
      {
        accessorKey: "accountRef",
        header: "Ref",
        footer: (props) => props.column.id,
      },        
      {
        accessorKey: "clientName",
        header: "Name",
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "clientAddr",
        header: "Address",
        footer: (props) => props.column.id,
      },          
      {
        accessorKey: "email",
        header: "Email",
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "phone",
        header: "Phone",
        footer: (props) => props.column.id,
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }: { row: { original: AccountDetail } }) => {
          const isDeleted = !!row.original.deletedDate;
          return isDeleted && props.canEdit !== false ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleUndelete(row.original.clientId!);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
              title="Restore this account"
            >
              <FaTrashRestore /> Restore
            </button>
          ) : null;
        },
        footer: (props) => props.column.id,
      },      
      // {
      //   accessorKey: "client",
      //   header: "Client",
      //   footer: (props) => props.column.id,
      // },      
      // {
      //   accessorKey: "site",
      //   header: "Site",
      //   footer: (props) => props.column.id,
      // },
      // {
      //   accessorKey: "description",
      //   header: "Description",
      //   footer: (props) => props.column.id,
      //   cell: ({ row }: { row: { original: AccountDetail } }) => (
      //     <Tooltip anchorElement="target" position="top">
      //       <span
      //         title={row.original.description}
      //         className="overflow__container"
      //       >
      //         {row.original.description}
      //       </span>
      //     </Tooltip>
      //   ),
      // },  
      // {
      //   accessorKey: "anniversary",
      //   header: "Due Date",
      //   cell: ({ row }: { row: { original: any } }) => (
      //     <span>
      //         {
      //           row.original.anniversary ? (
      //             row.original.notify3Sent  ? (
      //             <span className="circle__2">{dayjs(row.original.anniversary).format("DD-MM-YYYY")}</span>
      //             ) : row.original.notify1Sent || row.original.notify2Sent ? (
      //               <span className="circle__4">{dayjs(row.original.anniversary).format("DD-MM-YYYY")}</span>
      //             ) : row.original.notify1Sent === null ? (
      //               <span className="circle__3">{dayjs(row.original.anniversary).format("DD-MM-YYYY")}</span>
      //             ) : <span>what</span>
      //           ) : (
      //             "Missing date"
      //           )
      //         }
      //     </span>

      // ),          
      //   footer: (props) => props.column.id,
      // },
    ],
    [handleUndelete, props.canEdit]
  );

  const [data, setData] = useState<AccountDetail[]>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setData(props.onData);
  }, [props.onData]);

  const table = useReactTable({
    data,
    columns: clientColumns,
    filterFns: {
      fuzzy: fuzzyFilter,
      numberRange: numberRangeFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      pagination,
    },
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    autoResetPageIndex: false,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  // show deleted - kept original names for simplicity
  const [showDead, setShowDead] = useState(props.showDeleted || false);
  
  // Sync with parent's showDeleted prop
  useEffect(() => {
    console.log('🔄 Grid syncing showDead:', props.showDeleted);
    setShowDead(props.showDeleted || false);
  }, [props.showDeleted]);
  
  useEffect(() => {
    console.log('📊 Grid state - showDead:', showDead, 'props.showDeleted:', props.showDeleted, 'data length:', props.onData.length);
  }, [showDead, props.showDeleted, props.onData]);
  
  const handleShowDead = (event: any) => {
    console.log('🔘 Switch toggled - event:', event, 'event.value:', event.value, 'event.target.value:', event.target?.value);
    const newValue = event.value !== undefined ? event.value : event.target?.value;
    console.log('🔘 Setting showDead to:', newValue);
    setShowDead(newValue);
    props.onShowDead(newValue);
  };

  // show completed - kept original names for simplicity
  const [showProgressed, setShowProgressed] = useState(false);
  const handleShowProgressed = (event: any) => {
    const newValue = event.value !== undefined ? event.value : event.target?.value;
    setShowProgressed(newValue);
    if (props.onShowProgressed) {
      props.onShowProgressed(newValue);
    }
  };

  return (
    <div className="p-2">
      {/* This is the global column search box */}
      <div className="grid__title__container">
        <div className="grid__header__background">
          <div className="grid__title">
            <div style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}>
            <div>Accounts</div>
            <Switch
                className="grid__switch"
                size={"large"}
                onChange={handleShowDead}
                checked={showDead}
              /> 
              <Tooltip anchorElement="target" position="top" openDelay={0}>
                <FaCheckCircle
                  style={{
                    fontSize: "14pt",
                    marginLeft: "0.7rem",
                    color: showDead
                      ? "rgb(33, 67, 59)"
                      : "rgb(148 167 163)",
                  }}
                  title="Show Deleted Accounts"
                />
              </Tooltip>
            </div>            
            <div style={{display: "flex", flexDirection: "row"}}>
                {props.canEdit !== false && (
                  <div onClick={() => props.onAddToggle()} className="grid__addrecord__button">
                    <FaPlusCircle style={{ fontSize: "14pt", marginRight: "1rem" }} />{" "}
                    <p style={{ margin: 0 }}>Add new account</p>
                  </div>
                )}
              <div onClick={exportToExcel} className="grid__export__button">
                <FaFileExcel
                  style={{ fontSize: "14pt", marginRight: "1rem" }}
                />{" "}
                <p style={{ margin: 0 }}>Export accounts</p>
              </div>
            </div>
          </div>
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => setGlobalFilter(String(value))}
            placeholder="Search accounts..."
            className="filter__input__global"
          />
        </div>
      </div>

      <div className="h-2" />
      <table className="grid__table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <>
                        {/* This DIV is where you hover/click on the column header to sort */}
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "header__sortable"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: (
                              <FaSortAmountUpAlt style={{ fontSize: "14pt" }} />
                            ),
                            desc: (
                              <FaSortAmountDown style={{ fontSize: "14pt" }} />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <FaSort style={{ fontSize: "14pt" }} />
                          )}
                        </div>

                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const isDeleted = !!row.original.deletedDate;
            const bgColor = () => {
              if (isDeleted) {
                // More prominent red highlighting for deleted accounts
                return showDead ? "rgba(254, 202, 202, 0.7)" : "rgba(254, 226, 226, 0.5)";
              } else if (row.original.progressed) {
                return "rgba(215, 237, 215, 1)";
              } else {
                return "rgba(245, 249, 251, 1)";
              }
            };

            return (
              <tr
                onDoubleClick={() => props.onEditToggle(row.original.clientId)}
                style={{ 
                  backgroundColor: bgColor(),
                  borderLeft: isDeleted && showDead ? '4px solid #dc2626' : 'none'
                }}
                key={row.id}
              >
                {row.getVisibleCells().map((cell, index) => {
                  return (
                    <td key={cell.id} style={{ position: 'relative' }}>
                      {index === 0 && isDeleted && showDead && (
                        <span 
                          style={{
                            display: 'inline-block',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            marginRight: '8px',
                            verticalAlign: 'middle'
                          }}
                        >
                          DELETED
                        </span>
                      )}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* This is the pagination controls */}
      <div className="page__container">
        <div className="page__button__container">
          <button
            className="page__button"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
          <button
            className="page__button"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </button>
        </div>

        <div className="page__controls">
          <div className="page__numbers">
            <div>Page&nbsp;</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </div>
          <div className="page__numbers__goto">
            <div className="page__numbers__gototext">| Go to page:</div>
            <input
              type="number"
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="filter__input"
            />
          </div>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className="page__select"
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: Table<any>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column.getFacetedUniqueValues()]
  );

  return typeof firstValue === "number" ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0]
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1]
              ? `(${column.getFacetedMinMaxValues()?.[1]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : (
    <>
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.slice(0, 5000).map((value: any) => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        className="filter__input"
        list={column.id + "list"}
      />
    </>
  );
}

// A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

export default AccountsGrid;
